/**
 * Audio Player Context - Manages global audio playback state
 */
import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { playbackAPI, songsAPI } from '../services/api';
import { volumeNormalization } from '../services/volumeNormalization';

const AudioContext = createContext(null);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(new Audio());
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [playbackStateSynced, setPlaybackStateSynced] = useState(false);
  const [volumeNormalizationEnabled, setVolumeNormalizationEnabled] = useState(false);

  // Update URL with current song and position
  const updateURL = useCallback((songId, position = 0) => {
    try {
      const url = new URL(window.location);
      url.searchParams.set('songId', songId);
      if (position > 0) {
        url.searchParams.set('position', Math.floor(position).toString());
      } else {
        url.searchParams.delete('position');
      }
      window.history.replaceState({ songId, position }, '', url);
    } catch (error) {
      console.error('Failed to update URL:', error);
    }
  }, []);

  // Sync playback state with server
  const syncPlaybackState = useCallback(async (songId, position, playing) => {
    try {
      await playbackAPI.updateState(songId, position, playing);
      updateURL(songId, position);
    } catch (error) {
      console.error('Failed to sync playback state:', error);
    }
  }, [updateURL]);

  // Load and play a song
  const playSong = useCallback(async (song, songPlaylist = [], songIndex = 0) => {
    try {
      setIsLoading(true);

      // If new playlist provided, update it
      if (songPlaylist.length > 0) {
        setPlaylist(songPlaylist);
        setCurrentIndex(songIndex);
      }

      // Set current song
      setCurrentSong(song);

      // Update URL with current song info
      updateURL(song.id, 0);

      // Get streaming URL
      const streamUrl = songsAPI.streamSong(song.id);

      // Update audio source
      audioRef.current.src = streamUrl;
      audioRef.current.load();

      // Play the audio
      await audioRef.current.play();
      setIsPlaying(true);

      // Record play event
      await playbackAPI.recordEvent(song.id, 'play', 0);
      await syncPlaybackState(song.id, 0, true);

      setPlaybackStateSynced(true);
    } catch (error) {
      console.error('Error playing song:', error);
      setIsLoading(false);
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  }, [syncPlaybackState]);

  // Resume playback state from server
  const resumePlaybackState = useCallback(async () => {
    try {
      const response = await playbackAPI.getState();
      const state = response.data;

      if (state && state.song_id) {
        // Find the song in current playlist or load it
        const songInPlaylist = playlist.find(s => s.id === state.song_id);

        if (songInPlaylist) {
          setCurrentSong(songInPlaylist);
          setCurrentIndex(playlist.findIndex(s => s.id === state.song_id));

          const streamUrl = songsAPI.streamSong(state.song_id);
          audioRef.current.src = streamUrl;
          audioRef.current.currentTime = state.position;
          audioRef.current.load();

          if (state.is_playing) {
            audioRef.current.play();
            setIsPlaying(true);
          }
        }
      }
      setPlaybackStateSynced(true);
    } catch (error) {
      console.error('Failed to resume playback state:', error);
      setPlaybackStateSynced(true);
    }
  }, [playlist]);

  // Toggle play/pause
  const togglePlay = useCallback(async () => {
    if (!currentSong) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      await playbackAPI.recordEvent(currentSong.id, 'pause', currentTime);
      await syncPlaybackState(currentSong.id, currentTime, false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        await playbackAPI.recordEvent(currentSong.id, 'play', currentTime);
        await syncPlaybackState(currentSong.id, currentTime, true);
      } catch (error) {
        console.error('Error playing audio:', error);
      }
    }
  }, [currentSong, isPlaying, currentTime, syncPlaybackState]);

  // Skip to next song
  const skipToNext = useCallback(async () => {
    if (playlist.length === 0) return;

    // Record skip event
    if (currentSong) {
      await playbackAPI.recordEvent(currentSong.id, 'skip', currentTime);
    }

    let nextIndex;
    if (isShuffle) {
      nextIndex = Math.floor(Math.random() * playlist.length);
    } else {
      nextIndex = (currentIndex + 1) % playlist.length;
    }

    playSong(playlist[nextIndex], playlist, nextIndex);
  }, [playlist, currentIndex, isShuffle, currentSong, currentTime, playSong]);

  // Skip to previous song
  const skipToPrevious = useCallback(async () => {
    if (playlist.length === 0) return;

    // If more than 3 seconds played, restart current song
    if (currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }

    // Record skip event
    if (currentSong) {
      await playbackAPI.recordEvent(currentSong.id, 'skip', currentTime);
    }

    let prevIndex;
    if (isShuffle) {
      prevIndex = Math.floor(Math.random() * playlist.length);
    } else {
      prevIndex = (currentIndex - 1 + playlist.length) % playlist.length;
    }

    playSong(playlist[prevIndex], playlist, prevIndex);
  }, [playlist, currentIndex, isShuffle, currentSong, currentTime, playSong]);

  // Seek to position
  const seekTo = useCallback(async (time) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    updateURL(currentSong?.id, time);
    await playbackAPI.recordEvent(currentSong?.id, 'seek', time);
    await syncPlaybackState(currentSong?.id, time, isPlaying);
  }, [currentSong, isPlaying, syncPlaybackState, updateURL]);

  // Set volume
  const changeVolume = useCallback((newVolume) => {
    if (!audioRef.current) return;
    audioRef.current.volume = newVolume;
    setVolume(newVolume);
    if (newVolume > 0) {
      setIsMuted(false);
    }
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.volume = volume;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  }, [volume, isMuted]);

  // Toggle shuffle
  const toggleShuffle = useCallback(() => {
    setIsShuffle(!isShuffle);
  }, [isShuffle]);

  // Toggle repeat
  const toggleRepeat = useCallback(() => {
    setIsRepeat(!isRepeat);
  }, [isRepeat]);

  // Toggle volume normalization
  const toggleVolumeNormalization = useCallback(() => {
    const newState = !volumeNormalizationEnabled;
    setVolumeNormalizationEnabled(newState);
    volumeNormalization.setEnabled(newState);

    // Apply to current song if playing
    if (currentSong && audioRef.current) {
      if (newState) {
        // Apply normalization
        const streamUrl = songsAPI.streamSong(currentSong.id);
        volumeNormalization.preAnalyzeAudio(streamUrl).then(gain => {
          volumeNormalization.applyGainAdjustment(audioRef.current, gain);
        });
      } else {
        // Reset to normal volume
        audioRef.current.volume = volume;
      }
    }
  }, [volumeNormalizationEnabled, currentSong, volume]);

  // Handle URL parameters on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const songId = params.get('songId');
    const position = params.get('position');

    if (songId) {
      // Load song from URL parameters
      const loadFromURL = async () => {
        try {
          const response = await songsAPI.getAll();
          const song = response.data.find(s => s.id === parseInt(songId));

          if (song) {
            setPlaylist([song]);
            setCurrentIndex(0);
            setCurrentSong(song);

            const streamUrl = songsAPI.streamSong(song.id);
            audioRef.current.src = streamUrl;
            audioRef.current.load();

            if (position) {
              const pos = parseInt(position);
              audioRef.current.currentTime = pos;
              setCurrentTime(pos);
            }

            // Don't auto-play, let user click play
            setPlaybackStateSynced(true);
          }
        } catch (error) {
          console.error('Failed to load song from URL:', error);
          setPlaybackStateSynced(true);
        }
      };

      loadFromURL();
    } else {
      setPlaybackStateSynced(true);
    }
  }, []);

  // Setup audio event listeners
  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      // Update URL with current position every 5 seconds while playing
      if (isPlaying && currentSong && Math.floor(audio.currentTime) % 5 === 0) {
        updateURL(currentSong.id, audio.currentTime);
        syncPlaybackState(currentSong.id, audio.currentTime, true);
      }
    };

    const handleDurationChange = () => {
      setDuration(audio.duration);
    };

    const handleEnded = async () => {
      // Record completion event
      if (currentSong) {
        await playbackAPI.recordEvent(currentSong.id, 'complete', duration);
      }

      if (isRepeat) {
        // Repeat current song
        audio.currentTime = 0;
        audio.play();
      } else {
        // Play next song
        skipToNext();
      }
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);

      // Apply volume normalization if enabled
      if (volumeNormalizationEnabled && currentSong) {
        const streamUrl = songsAPI.streamSong(currentSong.id);
        volumeNormalization.preAnalyzeAudio(streamUrl).then(gain => {
          volumeNormalization.applyGainAdjustment(audio, gain);
        });
      }
    };

    const handleError = (error) => {
      console.error('Audio error:', error);
      setIsLoading(false);
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
    };
  }, [currentSong, isPlaying, isRepeat, duration, skipToNext, syncPlaybackState, volumeNormalizationEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioRef.current.pause();
      audioRef.current.src = '';
    };
  }, []);

  const value = {
    currentSong,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isShuffle,
    isRepeat,
    playlist,
    currentIndex,
    isLoading,
    playbackStateSynced,
    volumeNormalizationEnabled,
    playSong,
    pauseSong: () => { audioRef.current.pause(); setIsPlaying(false); },
    togglePlay,
    skipToNext,
    skipToPrevious,
    seekTo,
    changeVolume,
    toggleMute,
    toggleShuffle,
    toggleRepeat,
    toggleVolumeNormalization,
    resumePlaybackState,
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};